import React, { useState } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import SEOHead from './components/SEOHead';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import EmployerSummary from './components/EmployerSummary';
import ClinicalDashboard from './components/ClinicalDashboard';
import CertificationShowcase from './components/CertificationShowcase';
import JourneyStory from './components/JourneyStory';
import PatientInteraction from './components/PatientInteraction';
import ContactSection from './components/ContactSection';
import Footer from './components/Footer';
import { LanguageProvider } from './src/contexts/LanguageContext';
import SpotlightTour from './components/SpotlightTour';
import SpotlightWidget from './components/SpotlightWidget';
import { useSpotlightTour } from './hooks/useSpotlightTour';
import { MessageCircle, Download, Link2 } from 'lucide-react';
import { createWhatsAppUrl } from './utils/whatsapp';
import { downloadCV } from './utils/cvDownload';
import { useLanguage } from './src/contexts/LanguageContext';
// Import i18n configuration
import './src/i18n';
function AppContent() {
  const {
    language
  } = useLanguage();
  const [isConnectModalOpen, setIsConnectModalOpen] = useState(false);
  const {
    shouldShowTour,
    tourEnabled,
    isLoading,
    completeTour,
    dismissTour,
    restartTour,
    toggleTourEnabled
  } = useSpotlightTour();
  // Spotlight tour steps
  const spotlightSteps = [{
    id: 'whatsapp',
    target: '#whatsapp-cta',
    title: "Let's Connect on WhatsApp",
    description: 'Get instant responses to your questions about my experience and availability. The fastest way to start a conversation!',
    action: 'Message Me',
    icon: MessageCircle,
    onAction: () => {
      const message = `Hi Mona, I came across your profile and would like to discuss a job opportunity we have at:
- Company Name: [Your Company]
- Job Title: [Position Title]
- Compensation: [Salary Range]

Next Steps:
1. Please reply with your availability for a brief call
2. Share any questions you have about the role
3. We can schedule a formal interview

Looking forward to connecting!`;
      const whatsappUrl = createWhatsAppUrl('+358458426661', message);
      window.open(whatsappUrl, '_blank');
    },
    position: 'bottom' as const
  }, {
    id: 'download-cv',
    target: '#download-cv-btn',
    title: 'Download My Full CV',
    description: 'Get detailed credentials, certifications, and work history in one comprehensive PDF. Perfect for formal review.',
    action: 'Download CV',
    icon: Download,
    onAction: () => {
      downloadCV(language);
    },
    position: 'bottom' as const
  }, {
    id: 'connect-modal',
    target: '#connect-button',
    title: 'Multiple Ways to Connect',
    description: 'Book a video call, send an email, or message me directly. Choose the option that works best for you!',
    action: 'Explore Options',
    icon: Link2,
    onAction: () => {
      setIsConnectModalOpen(true);
    },
    position: 'bottom' as const
  }];
  // Widget quick actions
  const widgetActions = [{
    id: 'whatsapp',
    icon: MessageCircle,
    label: 'WhatsApp Me',
    onClick: () => {
      const message = `Hi Mona, I came across your profile and would like to discuss a job opportunity we have at:
- Company Name: [Your Company]
- Job Title: [Position Title]
- Compensation: [Salary Range]

Next Steps:
1. Please reply with your availability for a brief call
2. Share any questions you have about the role
3. We can schedule a formal interview

Looking forward to connecting!`;
      const whatsappUrl = createWhatsAppUrl('+358458426661', message);
      window.open(whatsappUrl, '_blank');
    },
    color: 'bg-sage-mint/20'
  }, {
    id: 'download-cv',
    icon: Download,
    label: 'Download CV',
    onClick: () => {
      downloadCV(language);
    },
    color: 'bg-coral-pink/20'
  }, {
    id: 'connect',
    icon: Link2,
    label: 'Connect Options',
    onClick: () => {
      setIsConnectModalOpen(true);
    },
    color: 'bg-sage-mint/20'
  }];
  return <div className="bg-cream-white min-h-screen w-full" data-id="element-0">
      <Navbar data-id="element-1" />
      <Hero data-id="element-2" />
      <EmployerSummary data-id="element-3" />
      <ClinicalDashboard data-id="element-4" />
      <CertificationShowcase data-id="element-5" />
      <JourneyStory data-id="element-6" />
      <PatientInteraction data-id="element-7" />
      <ContactSection data-id="element-8" />
      <Footer data-id="element-9" />

      {/* Spotlight Tour */}
      {!isLoading && shouldShowTour && <SpotlightTour steps={spotlightSteps} onComplete={completeTour} onDismiss={dismissTour} data-id="element-10" />}

      {/* Spotlight Widget - Always visible after tour */}
      {!isLoading && <SpotlightWidget actions={widgetActions} onRestartTour={restartTour} onToggleTour={toggleTourEnabled} tourEnabled={tourEnabled} data-id="element-11" />}
    </div>;
}
export function App() {
  return <HelmetProvider data-id="element-12">
      <LanguageProvider data-id="element-13">
        <SEOHead data-id="element-14" />
        <AppContent data-id="element-15" />
      </LanguageProvider>
    </HelmetProvider>;
}